import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoComplete, Table, Button, Input } from 'antd';
import { Download, Filter, FilterX, Store, Search, MapPin } from 'lucide-react';
import { StatusTag, Toolbar, EmptyState, CollapsibleCard } from '../../../common';
import { useStoresTable } from './storesTable.controller';

function StoresTable({ stores, setFiltered, filtered, loading, setSelectedStores, selectedStores, storeChecks, setStoreChecks }) {
    const navigate = useNavigate();
    const { suggestions, rowKeys, onSearch, download, onFilter, removeFilter, storesSelection, dataWithKeys } = useStoresTable({
        stores,
        setFiltered,
        filtered,
        setSelectedStores,
        selectedStores,
        storeChecks,
        setStoreChecks
    });

    const StoreColumns = [
        {
            title: 'Piso de Venta',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div className="min-w-0">
                    <button
                        type="button"
                        onClick={() => navigate(`/dashboard/stores/store?id=${record._id}`)}
                        className="block text-left text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors">
                        {text}
                    </button>
                    <span className="block text-xs text-surface-500">{record.storeType || 'Sin tipo'}</span>
                </div>
            ),
            sorter: (a, b) => (a.name || '').localeCompare(b.name || '')
        },
        {
            title: 'RIF',
            dataIndex: 'rif',
            key: 'rif',
            render: (text) => <span className="text-sm text-surface-600 tabular-nums">{text || '--'}</span>
        },
        {
            title: 'Coordenadas',
            dataIndex: 'coordinates',
            key: 'coordinates',
            render: (coords) =>
                coords?.latitude && coords?.longitude ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-surface-500 tabular-nums">
                        <MapPin className="text-surface-400" />
                        {coords.latitude.toFixed(4)}, {coords.longitude.toFixed(4)}
                    </span>
                ) : (
                    <span className="text-xs text-surface-300">Sin coordenadas</span>
                )
        },
        {
            title: 'Estado',
            dataIndex: 'active',
            key: 'active',
            render: (active) => (
                <StatusTag tone={active ? 'success' : 'danger'} dot>
                    {active ? 'Activo' : 'Inactivo'}
                </StatusTag>
            ),
            filters: [
                { text: 'Activo', value: true },
                { text: 'Inactivo', value: false }
            ],
            onFilter: (value, record) => record.active === value
        }
    ];

    return (
        <CollapsibleCard flush icon={<Store />} iconTone="brand" title="Pisos de Venta" count={`${stores?.length || 0} registrados`} countTone="brand">
            <div className="px-5 pt-4">
                <Toolbar
                    className="!mb-3"
                    left={
                        <AutoComplete
                            options={suggestions.map((suggestion) => ({ value: suggestion }))}
                            onSearch={onSearch}
                            onSelect={onSearch}
                            className="w-full sm:w-72"
                            allowClear
                            notFoundContent="No se encontraron resultados"
                            filterOption={false}>
                            <Input placeholder="Buscar piso de venta..." prefix={<Search className="text-surface-400" />} />
                        </AutoComplete>
                    }
                    right={
                        <Button icon={<Download />} onClick={download}>
                            Exportar
                        </Button>
                    }
                />

                {/* Selection → filter the profile's audits */}
                <div className="mb-4 flex flex-col gap-3 rounded-lg border border-highlight-border bg-highlight-bg px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm text-surface-500">
                        <strong className="text-surface-900 tabular-nums">{rowKeys.length}</strong> seleccionados
                        <span className="hidden md:inline"> · selecciona pisos para filtrar los datos del perfil</span>
                    </span>
                    <div className="flex items-center gap-2">
                        {rowKeys.length > 0 && selectedStores.length > 0 && (
                            <Button icon={<FilterX />} onClick={() => removeFilter()}>
                                Quitar Filtro
                            </Button>
                        )}
                        <Button type="primary" icon={<Filter />} onClick={() => onFilter()} disabled={rowKeys.length === 0}>
                            Filtrar Datos
                        </Button>
                    </div>
                </div>
            </div>

            <Table
                columns={StoreColumns}
                dataSource={dataWithKeys}
                rowSelection={storesSelection}
                loading={loading}
                size="middle"
                scroll={{ x: 640 }}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} pisos`
                }}
                locale={{ emptyText: <EmptyState icon={<Store />} title="Sin pisos de venta" description="No hay pisos que coincidan con la búsqueda." /> }}
            />
        </CollapsibleCard>
    );
}

export default StoresTable;
